import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Field, Input, notify } from '../../../components/ui';
import { useApiError } from '../../../hooks/useApiError';
import api, { getApiError } from '../../../services/api';
import type { Table } from '../../../types/Table';
import { MAX_LABEL } from '../tableInput';

interface Props {
    table: Table | null;
    onClose: () => void;
    onSaved: () => void;
}

const Form = ({ table, onClose, onSaved }: Props & { table: Table }) => {
    const { t } = useTranslation('locations');
    const apiError = useApiError();
    const [label, setLabel] = useState(table.tableNumber ?? '');
    const [description, setDescription] = useState(table.description ?? '');
    const [error, setError] = useState<string>();
    const [busy, setBusy] = useState(false);

    const save = async () => {
        const tableNumber = label.trim() || null;
        if (tableNumber && tableNumber.length > MAX_LABEL) {
            setError(t('tables.errors.tooLong', { maxLabel: MAX_LABEL }));
            return;
        }
        setBusy(true);
        try {
            await api.put(`/tables/${table.id}`, { tableNumber, description: description.trim() });
            notify.success(t('tables.saved'));
            onSaved();
            onClose();
        } catch (e) {
            if (getApiError(e).status === 409) setError(t('tables.duplicate'));
            else notify.error(apiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog
            open
            onClose={onClose}
            busy={busy}
            title={table.tableNumber ? t('tables.editTitle', { name: table.tableNumber }) : t('tables.editTitleCode', { code: table.code })}
            footer={
                <>
                    <Button variant="quiet" onClick={onClose} disabled={busy}>
                        {t('common:ui.cancel')}
                    </Button>
                    <Button variant="primary" onClick={save} loading={busy}>
                        {t('common:ui.save')}
                    </Button>
                </>
            }
        >
            <form
                className="flex flex-col gap-4"
                onSubmit={(e) => {
                    e.preventDefault();
                    void save();
                }}
            >
                <Field label={t('tables.label')} hint={t('tables.renameHint')} error={error} optional>
                    {(c) => <Input {...c} data-autofocus value={label} onChange={(e) => setLabel(e.target.value)} maxLength={MAX_LABEL} autoComplete="off" />}
                </Field>
                <Field label={t('tables.description')} optional>
                    {(c) => <Input {...c} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={100} autoComplete="off" />}
                </Field>
                <button type="submit" className="hidden" tabIndex={-1} aria-hidden="true" />
            </form>
        </Dialog>
    );
};

/** Mounted only while a table is being edited, so the form starts from that table. */
export const TableEditDialog = (props: Props) => (props.table ? <Form {...props} table={props.table} /> : null);
